import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './registrationcss/Register.css';
import './registrationcss/AccompanistContactDetails.css';
import RegistrationProgressBar from '../../components/usercomponents/RegistrationProgressBar';
import accompanistContactImg from '../../images/ContactDetails_IMG.png';
import logo from '../../images/auravit_logo.png';

const relationshipOptions = [
  { value: 'family', label: 'Family' },
  { value: 'friend', label: 'Friend' },
  { value: 'caregiver', label: 'Caregiver' },
  { value: 'healthworker', label: 'Health Worker' },
  { value: 'others', label: 'Others' }
];

export default function AccompanistContactDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // State management
  const [formData, setFormData] = useState({
    accompanistFullName: '',
    relationshipToUser: '',
    relationshipOther: '',
    contactNumber: '',
    accompanistAddress: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  // Hydrate from saved draft when navigating back
  useEffect(() => {
    const draft = JSON.parse(localStorage.getItem('registrationDraft') || '{}');
    if (!draft.accompanistFullName && !draft.accompanistContactNumber && !draft.accompanistAddress) return;

    // accompanistContactNumber is stored as +63XXXXXXXXXX — strip prefix for the input
    const rawContact = (draft.accompanistContactNumber || '').replace(/^\+63/, '');

    // accompanistRelationshipToUser may be a custom value (others) or a preset option
    const presetValues = relationshipOptions.map(o => o.value);
    const isPreset = presetValues.includes(draft.accompanistRelationshipToUser);
    const relationshipToUser = isPreset ? draft.accompanistRelationshipToUser : (draft.accompanistRelationshipToUser ? 'others' : '');
    const relationshipOther = !isPreset ? (draft.accompanistRelationshipToUser || '') : '';

    setFormData({
      accompanistFullName: draft.accompanistFullName || '',
      relationshipToUser,
      relationshipOther,
      contactNumber: rawContact,
      accompanistAddress: draft.accompanistAddress || '',
    });
  }, []);

  // Get user data from location state
  const userContact = location.state?.contact || '';

  // Validation functions
  const validateName = (value) => {
    if (!value.trim()) return "Full name is required";
    if (!/^[A-Za-z\s'-]*$/.test(value)) return "Invalid name format";
    if (value.trim().length > 50) return "Maximum 50 characters";
    return "";
  };

  const validateContact = (value) => {
    const digits = value.replace(/[^\d]/g, '');
    if (!value) return "Contact number is required";
    if (digits.length !== 10) return "Contact number must be 10 digits (e.g., 9123456789)";
    if (!digits.startsWith('9')) return "Contact number must start with 9";
    if (digits === userContact.replace(/[^\d]/g, '')) return "Contact number cannot be the same as user contact number";
    return "";
  };

  const validateAddress = (value) => {
    if (!value.trim()) return "Address is required";
    if (value.trim().length < 5) return "Address must be at least 5 characters";
    if (value.trim().length > 100) return "Address must be less than 100 characters";
    if (!/^[A-Za-z0-9\s'-]*$/.test(value)) return "Address can only contain letters, numbers, spaces, and hyphens";
    return "";
  };

  const validateRelationship = (value) => {
    if (!value) return "Relationship is required";
    if (value === 'others' && !formData.relationshipOther.trim()) return "Please specify relationship";
    return "";
  };

  // Handle input changes
  const handleChange = (field, value) => {
    let processedValue = value;
    
    if (field === 'accompanistFullName') {
      processedValue = value.replace(/[^A-Za-z\s'-]/g, '').slice(0, 50);
      setErrors(prev => ({ ...prev, accompanistFullName: validateName(processedValue) }));
    } else if (field === 'accompanistAddress') {
      processedValue = value.replace(/[^A-Za-z0-9\s'-]/g, '').slice(0, 100);
      setErrors(prev => ({ ...prev, accompanistAddress: validateAddress(processedValue) }));
    } else if (field === 'contactNumber') {
      processedValue = value.replace(/[^\d]/g, '').slice(0, 10);
      setErrors(prev => ({ ...prev, contactNumber: validateContact(processedValue) }));
    } else if (field === 'relationshipOther') {
      processedValue = value.replace(/[^A-Za-z\s'-]/g, '').slice(0, 20);
      setErrors(prev => ({ ...prev, relationshipToUser: validateRelationship(formData.relationshipToUser) }));
    }

    setFormData(prev => ({ ...prev, [field]: processedValue }));
  };

  // Handle dropdown selection
  const handleDropdownSelect = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setOpenDropdown(null);
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    newErrors.accompanistFullName = validateName(formData.accompanistFullName);
    newErrors.relationshipToUser = validateRelationship(formData.relationshipToUser);
    newErrors.contactNumber = validateContact(formData.contactNumber);
    newErrors.accompanistAddress = validateAddress(formData.accompanistAddress);

    setErrors(newErrors);
    return Object.values(newErrors).every(error => !error);
  };

  // Handle form submission
  const handleContinue = async () => {
    if (!validateForm()) return;
    if (isSaving) return;

    setIsSaving(true);
    try {
      // Store in localStorage for registration flow
      const storedDraft = JSON.parse(localStorage.getItem('registrationDraft') || '{}');
      const updatedDraft = {
        ...storedDraft,
        ...location.state,
        accompanistFullName: formData.accompanistFullName.trim(),
        accompanistRelationshipToUser: formData.relationshipToUser === 'others' ? formData.relationshipOther.trim() : formData.relationshipToUser,
        accompanistContactNumber: formData.contactNumber ? `+63${formData.contactNumber}` : '',
        accompanistAddress: formData.accompanistAddress.trim(),
        // Clear guardian details when saving accompanist details
        guardianFullName: '',
        guardianRelationshipToUser: '',
        guardianContactNumber: '',
        registrationStep: 4,
      };

      localStorage.setItem('registrationDraft', JSON.stringify(updatedDraft));

      // Navigate to next step
      navigate('/account-setup', {
        state: updatedDraft
      });
    } catch (err) {
      console.error('Accompanist contact save error:', err);
      alert('Failed to save accompanist information. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };


  return (
    <>
      <div className="login-logo-container">
        <img 
          src={logo} 
          alt="AuraVit" 
          className="login-logo"
          onClick={() => navigate("/")}
        />
      </div>
      <div className="register-container">
        <div className="register-card">
          <div className="left-panel">
            <img
              src={accompanistContactImg}
              alt="Accompanist Contact"
              className="panel-image"
            />
          </div>
          <div className="right-panel">
            <div className="registration-header-left">
              <h1 className="create-account-title">Create Account</h1>
              <p className="wellness-subtitle">
                If you'll be visiting with someone, we'll need their contact information for coordination.
              </p>
            </div>

            <RegistrationProgressBar currentStep={4} />

            {/* Display submit error if backend save fails */}
            {errors.submit && (
              <div className="alert alert-error" role="alert">
                {errors.submit}
              </div>
            )}

            <form noValidate>

              {/* Accompanist Full Name */}
            <div className="form-row">
              <div className="input-group">
                <label htmlFor="accompanistFullName">
                  Accompanist Full Name <span className="required">*</span>
                </label>
                <input
                  id="accompanistFullName"
                  type="text"
                  name="accompanistFullName"
                  placeholder="Enter accompanist's full name"
                  value={formData.accompanistFullName}
                  onChange={(e) => handleChange('accompanistFullName', e.target.value)}
                  className={errors.accompanistFullName ? 'error' : ''}
                  maxLength="50"
                  disabled={isSaving}
                  required
                />
                {errors.accompanistFullName && <span className="field-error">{errors.accompanistFullName}</span>}
              </div>
            </div>

            {/* Relationship to User and Accompanist Contact Number */}
            <div className="form-row">
              <div className="input-group">
                <label htmlFor="relationshipToUser">
                  Relationship to User <span className="required">*</span>
                </label>
                <div className="custom-select-wrapper" style={{ width: '100%', position: 'relative' }}>
                  <select
                    id="relationshipToUser"
                    name="relationshipToUser"
                    value={formData.relationshipToUser === 'others' ? 'Other' : formData.relationshipToUser}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === 'Other') {
                        setFormData(prev => ({ ...prev, relationshipToUser: 'others', relationshipOther: '' }));
                      } else {
                        setFormData(prev => ({ ...prev, relationshipToUser: value, relationshipOther: '' }));
                      }
                      // Clear error when selection is made
                      setErrors(prev => ({ ...prev, relationshipToUser: '' }));
                    }}
                    className={errors.relationshipToUser ? 'error' : ''}
                    disabled={isSaving}
                    required
                    style={{ 
                      border: '2px solid #e1e5e9',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      fontSize: '1rem',
                      backgroundColor: '#fafafa',
                      width: '100%',
                      appearance: 'none',
                      backgroundImage: 'none',
                      height: '48px',
                      boxSizing: 'border-box',
                      textAlign: 'left'
                    }}
                  >
                    <option value="" disabled hidden>Select Relationship</option>
                    {relationshipOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>

                  {formData.relationshipToUser === 'others' && (
                    <input
                      type="text"
                      name="relationshipOther"
                      value={formData.relationshipOther}
                      onChange={(e) => handleChange('relationshipOther', e.target.value)}
                      className={errors.relationshipToUser ? 'error' : ''}
                      placeholder="Specify relationship"
                      maxLength="20"
                      disabled={isSaving}
                      autoFocus
                      style={{ marginTop: '10px' }}
                    />
                  )}
                  <span className="dropdown-icon" />
                </div>
                {errors.relationshipToUser && <div className="field-error">{errors.relationshipToUser}</div>}
              </div>

              <div className="input-group">
                <label htmlFor="contactNumber">
                  Accompanist Contact Number <span className="required">*</span>
                </label>
                <div className="contact-wrapper">
                  <span className="country-code">+63</span>
                  <input
                    id="contactNumber"
                    type="text"
                    name="contactNumber"
                    placeholder="9123456789"
                    value={formData.contactNumber}
                    onChange={(e) => handleChange('contactNumber', e.target.value)}
                    className={errors.contactNumber ? 'error' : ''}
                    maxLength="10"
                    disabled={isSaving}
                    inputMode="numeric"
                    required
                  />
                </div>
                {errors.contactNumber && <div className="field-error">{errors.contactNumber}</div>}
              </div>
            </div>

            {/* Accompanist Address */}
            <div className="form-row">
              <div className="input-group">
                <label htmlFor="accompanistAddress">
                  Accompanist Address <span className="required">*</span>
                </label>
                <input
                  id="accompanistAddress"
                  type="text"
                  name="accompanistAddress"
                  placeholder="Enter accompanist's address"
                  value={formData.accompanistAddress}
                  onChange={(e) => handleChange('accompanistAddress', e.target.value)}
                  className={errors.accompanistAddress ? 'error' : ''}
                  maxLength="100"
                  disabled={isSaving}
                  required
                />
                {errors.accompanistAddress && <div className="field-error">{errors.accompanistAddress}</div>}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="form-actions">
              <button
                type="button"
                className="continue-btn"
                onClick={handleContinue}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Continue'}
              </button>
            </div>
          </form>
          </div>
        </div>
      </div>
    </>
  );
}
